import { useRouter as useCompatRouter } from "next/compat/router"
import {
  useRouter as useAppRouter,
  useSearchParams as useAppSearchParams,
} from "next/navigation"
import { useCallback, useMemo } from "react"

import { webUrlToString } from "../lib/webUrl"

import type { QueryParams, WebUrl } from "../lib/webUrl"

interface UseUrlQueryRes {
  query: QueryParams
  pushQuery: (query: QueryParams) => void
  replaceQuery: (query: QueryParams) => void
  push: (href: string | WebUrl) => void
}

/**
 * Router-agnostic access to the URL query string, usable from both the Pages
 * Router (web) and the App Router (web-cxa, web-admin).
 *
 * query: A QueryParams object
 * pushQuery: shallow update the query with history
 * replaceQuery: shallow replace the query with no history
 * push: navigates to a new URL
 */
export function useUrlQuery(): UseUrlQueryRes {
  const pagesRouter = useCompatRouter() // null in app router
  const appRouter = useAppRouter()
  const appSearchParams = useAppSearchParams()

  // Convert the App Router's URLSearchParams into a NextRouter QueryParams.
  const appQuery = useMemo(() => {
    const query: QueryParams = {}
    appSearchParams?.forEach((_, key) => {
      const values = appSearchParams.getAll(key)
      query[key] = values.length > 1 ? values : values[0]
    })
    return query
  }, [appSearchParams])

  const setQuery = useCallback(
    (query: QueryParams, mode: "push" | "replace") => {
      if (pagesRouter) {
        const url = { pathname: pagesRouter.pathname, query }
        if (mode === "push") {
          pagesRouter.push(url, undefined, { shallow: true })
        } else {
          pagesRouter.replace(url, undefined, { shallow: true })
        }
        return
      }
      const href = webUrlToString({ pathname: window.location.pathname, query })
      if (mode === "push") {
        window.history.pushState(null, "", href)
      } else {
        window.history.replaceState(null, "", href)
      }
    },
    [pagesRouter]
  )

  const pushQuery = useCallback(
    (query: QueryParams) => setQuery(query, "push"),
    [setQuery]
  )

  const replaceQuery = useCallback(
    (query: QueryParams) => setQuery(query, "replace"),
    [setQuery]
  )

  const push = useCallback(
    (href: string | WebUrl) => {
      if (pagesRouter) {
        pagesRouter.push(href)
      } else {
        appRouter.push(typeof href === "string" ? href : webUrlToString(href))
      }
    },
    [pagesRouter, appRouter]
  )

  return {
    query: pagesRouter ? pagesRouter.query : appQuery,
    pushQuery,
    replaceQuery,
    push,
  }
}
